import {DialogContent, Stack, Typography, useTheme} from '@mui/material';

import {IconBadge, useAlertDialog} from 'components';

import {BaseDialog} from '@resurs-internal/mui';

import {useTranslate} from 'hooks/useTranslate';
import {useNavigate} from '@tanstack/react-router';
import {useDialog} from 'hooks/useDialog/useDialog';
import {useDialogProvider} from 'hooks/useDialog/DialogProvider';
import type {SavingsWithdrawalDialogState} from 'features/deposit/withdrawal/SavingsAccountWithdrawalDialog';
import {useDepositAccount, useCountry} from '@resurs-internal/rb-app-api';

import {createBankIDUrl} from 'services/createBankIDUrl';

export type SavingsAccountsClose = {id: string | undefined};

export const SavingsAccountsCloseDialog = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();
  const dialog = useDialog<SavingsAccountsClose>('accounts-savings-close');
  const dialogProvider = useDialogProvider<SavingsWithdrawalDialogState>();
  const alert = useAlertDialog();
  const id = dialog.state?.id;
  const country = useCountry();
  const account = useDepositAccount(id);
  const disbursementId = account.query.data?.disbursementAccount?.id;

  const onSubmit = async () => {
    if (!disbursementId || !id) {
      return handleWarning();
    }
    const successRedirect = `${window.location.origin}/savings-close/${id}/${disbursementId}?status=SUCCESSFUL`;
    const cancelRedirect = `${window.location.origin}/savings-close/${id}/${disbursementId}?status=ERROR`;
    try {
      await account.close.mutateAsync(
        {
          disbursementAccountId: disbursementId,
          locale: country.locale,
          onSuccessUri: successRedirect,
          onCancelledUri: cancelRedirect,
        },
        {
          onSuccess: (response) => {
            const isSEBankID = country.code == 'SE';
            if (isSEBankID) {
              const token = response.signatureId;
              const bankIDURL = createBankIDUrl({
                token,
                redirect: `/close/${id}/${disbursementId}`,
                accountId: id,
              });
              navigate({to: bankIDURL});
              return;
            }
            const redirect = response?.redirect;
            if (redirect) {
              window.location.replace(redirect);
            } else {
              handleError();
            }
          },
          onError: () => {
            handleError();
          },
        },
      );
    } catch (error) {
      handleError();
    }
  };

  const handleWarning = () => {
    dialog.onClose();
    alert.setState({
      icon: 'triangle-exclamation',
      variant: 'warning',
      title: translate(
        'savings.close-account.failed.missing-withdrawal-account',
      ),
      subtitle: translate(
        'savings.close-account.failed.missing-withdrawal-account-subtitle',
      ),
      actions: [
        {
          title: translate('common.cancel'),
          onClick: alert.onClose,
        },
        {
          title: translate('savings.close-account.add-withdrawal-account'),
          onClick: () => {
            alert.onClose();
            dialogProvider.setState({
              type: 'savings-withdrawal',
              accountId: id ?? '',
            });
          },
          color: 'primary',
        },
      ],
    });
  };

  const handleError = () => {
    dialog.onClose();
    alert.setState({
      icon: 'triangle-exclamation',
      variant: 'warning',
      title: translate('savings.close-account.failed-title'),
      subtitle: translate('savings.close-account.failed-subtitle'),
      actions: [
        {
          title: translate('common.close'),
          onClick: () => {
            alert.onClose();
          },
          color: 'primary',
        },
      ],
    });
  };

  return (
    <BaseDialog
      open={dialog.open}
      onClose={dialog.onClose}
      maxWidth={'sm'}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: dialog.onClose,
        },
        {
          color: 'primary',
          title: translate('savings.close-account.title'),
          onClick: onSubmit,
        },
      ]}>
      <DialogContent>
        <Stack spacing={3} alignItems={'center'}>
          <IconBadge
            type={'font'}
            icon={'circle-exclamation'}
            color={theme.palette.error.main}
            size={'xl'}
          />
          <Typography p={2} textAlign="center" variant="h3">
            {translate('savings.close-account.dialog-title')}
          </Typography>
          <Typography
            variant={'body1'}
            mb={4}
            whiteSpace={'break-spaces'}
            color={theme.palette.text.secondary}>
            {translate('savings.close-account.dialog-body')}
          </Typography>
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
